// src/components/OrderForm.js
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { createOrderSuccess } from '../store/reducers/productSlice';
import LoginModal from './LoginModal';
import AlertModal from './AlertModal';
import styles from '../styles/OrderForm.module.css';
import { submitOrder, checkOrderStatus } from '../api/productApi';

const OrderForm = ({ product, isPaymentModalOpen, setIsPaymentModalOpen, isPaymentInProgress, setIsPaymentInProgress, onPaymentComplete }) => {
  const [paymentMethod, setPaymentMethod] = useState('alipay');
  const [notes, setNotes] = useState('');
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [pendingOrder, setPendingOrder] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.user.user);
  const intervalRef = useRef(null); // 用于存储轮询的 interval ID

  const queryParams = new URLSearchParams(location.search);
  const productId = queryParams.get('productId');

  console.log('Current user info:', user);
  console.log('URL productId:', productId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsPaymentInProgress(true); // 立即禁用按钮，实现即时变灰效果
    try {
      console.log('User info before submitting order:', user);

      const domain = window.location.hostname;
      const orderData = {
        product_id: productId,
        quantity: 1,
        paymentMethod: paymentMethod || 'alipay',
        notes: notes || '',
        price: product.price,
        uid: user?.uid,
        user_key: user?.user_key,
        domain: domain,
      };

      if (!user || !user.uid || !user.user_key) {
        console.log('User not logged in or missing user_key, opening login modal');
        setPendingOrder(orderData);
        setIsLoginModalOpen(true);
        setIsPaymentInProgress(false); // 如果需要登录，恢复按钮状态
        return;
      }

      console.log('Submitting order:', orderData);
      const response = await submitOrder(orderData); // 直接调用 API
      const order = response.data; // 获取返回数据
      console.log('Raw order response:', order);

      // 更新 Redux 状态（可选）
      dispatch(createOrderSuccess(order));

      if (order && typeof order.payUrl === 'string' && order.payUrl.length > 0) {
        console.log('Payment URL valid, opening new window:', order.payUrl);
        window.open(order.payUrl, '_blank');
        setIsPaymentModalOpen(true); // 使用外部传入的 setIsPaymentModalOpen 更新状态
        checkPaymentStatus(order.out_trade_no);
      } else {
        console.log('Invalid payUrl:', order);
        throw new Error('支付申请失败，未返回有效的 payUrl');
      }
    } catch (error) {
      console.error('支付失败:', error);
      alert(`支付失败: ${error.message || '请稍后重试'}`);
      setIsPaymentModalOpen(false); // 支付失败时关闭弹窗
      setIsPaymentInProgress(false); // 支付失败时恢复按钮状态
      if (onPaymentComplete) {
        onPaymentComplete(); // 通知上层组件支付结束，恢复按钮状态
      }
    }
  };

  // 登录成功后的回调
  const handleLoginSuccess = async () => {
    setIsLoginModalOpen(false);
    if (pendingOrder) {
      try {
        const domain = window.location.hostname;
        console.log('User logged in, submitting pending order:', pendingOrder);
        const updatedOrderData = { 
          ...pendingOrder, 
          uid: user?.uid, 
          user_key: user?.user_key,
          domain: domain
        };
        const response = await submitOrder(updatedOrderData); // 直接调用 API
        const order = response.data;
        console.log('Raw order response:', order);

        dispatch(createOrderSuccess(order));

        if (order && typeof order.payUrl === 'string' && order.payUrl.length > 0) {
          console.log('Payment URL valid, opening new window:', order.payUrl);
          window.open(order.payUrl, '_blank');
          setIsPaymentModalOpen(true); // 使用外部传入的 setIsPaymentModalOpen 更新状态
          checkPaymentStatus(order.out_trade_no);
        } else {
          console.log('Invalid payUrl:', order);
          throw new Error('支付申请失败，未返回有效的 payUrl');
        }
      } catch (error) {
        console.error('支付失败:', error);
        alert(`支付失败: ${error.message || '请稍后重试'}`);
        setIsPaymentModalOpen(false); // 支付失败时关闭弹窗
        setIsPaymentInProgress(false); // 支付失败时恢复按钮状态
        if (onPaymentComplete) {
          onPaymentComplete(); // 通知上层组件支付结束，恢复按钮状态
        }
      }
      setPendingOrder(null);
    }
  };

  // 关闭登录弹窗
  const handleCloseModal = () => {
    setIsLoginModalOpen(false);
    setPendingOrder(null);
  };

  // 关闭支付提示弹窗，并停止轮询
  const handlePaymentModalClose = () => {
    setIsPaymentModalOpen(false); // 使用外部传入的 setIsPaymentModalOpen 更新状态
    if (intervalRef.current) {
      clearInterval(intervalRef.current); // 关闭弹窗时停止轮询
      intervalRef.current = null;
    }
    setIsPaymentInProgress(false); // 弹窗关闭时恢复按钮状态
    if (onPaymentComplete) {
      onPaymentComplete(); // 通知上层组件支付取消，恢复按钮状态
    }
  };

  // 轮询支付状态
  const checkPaymentStatus = (out_trade_no) => {
    intervalRef.current = setInterval(async () => {
      try {
        const response = await checkOrderStatus(out_trade_no);
        console.log('Payment status response:', response.data);
        if (response.data.pay_result === 'success') { // 根据 pay_result 判断支付成功
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          setIsPaymentModalOpen(false); // 关闭支付弹窗
          setIsPaymentInProgress(false); // 恢复按钮状态
          // 将 out_trade_no 添加到 URL 路径中
          navigate(`/order-confirmation/${out_trade_no}`, {
            state: { product, paymentMethod: paymentMethod || 'alipay' },
          });
          if (onPaymentComplete) {
            onPaymentComplete(); // 通知上层组件支付完成，恢复按钮状态
          }
        } else {
          console.log('Payment not yet successful, continuing polling...');
          // 如果 pay_result 不是 success，继续轮询
        }
      } catch (error) {
        console.error('检查支付状态失败:', error);
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        setIsPaymentModalOpen(false); // 使用外部传入的 setIsPaymentModalOpen 更新状态
        setIsPaymentInProgress(false); // 支付失败时恢复按钮状态
        alert('检查支付状态失败，请稍后重试');
        if (onPaymentComplete) {
          onPaymentComplete(); // 通知上层组件支付失败，恢复按钮状态
        }
      }
    }, 3000); // 每 3 秒轮询一次
  };

  // 在组件卸载或网页关闭时停止轮询
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current); // 网页关闭时停止轮询
        intervalRef.current = null;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // 清理函数，在组件卸载时移除事件监听器
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (intervalRef.current) {
        clearInterval(intervalRef.current); // 组件卸载时停止轮询
        intervalRef.current = null;
      }
    };
  }, []);

  return (
    <div className={styles.orderForm}>
      <h2>订单申请</h2>
      <div className={styles.alert}>
        <i className="fas fa-info-circle"></i>
        <div>
          <strong>温馨提示：</strong>账户信息将在付款成功后通过邮件和账户中心发送给您。请确保您的联系信息准确无误。
        </div>
      </div>

      <form id="orderForm" onSubmit={handleSubmit}>
        <div className={styles.formSection}>
          <h3>
            <i className="fas fa-user"></i> 个人信息
          </h3>
          <div className={styles.formGroup}>
            <label htmlFor="email">登录邮箱</label>
            <input
              type="email"
              id="email"
              name="email"
              value={user?.email || 'user@example.com'}
              readOnly
              className={styles.readonlyInput}
            />
            <small>您的登录邮箱信息不可修改</small>
          </div>
        </div>

        <div className={styles.formSection}>
          <h3>
            <i className="fas fa-credit-card"></i> 支付渠道
          </h3>
          <p>请选择您偏好的支付渠道</p>
          <div className={styles.paymentOptions}>
            {[
              { id: 'alipay', icon: 'fab fa-alipay', label: '支付宝' },
              { id: 'wechat', icon: 'fab fa-weixin', label: '微信支付' },
              { id: 'visa', icon: 'fab fa-cc-visa', label: 'Visa' },
              { id: 'mastercard', icon: 'fab fa-cc-mastercard', label: 'Mastercard' },
              { id: 'paypal', icon: 'fab fa-paypal', label: 'PayPal' },
              { id: 'crypto', icon: 'fab fa-bitcoin', label: '加密货币' },
            ].map(option => (
              <div key={option.id} className={styles.paymentOption}>
                <input
                  type="radio"
                  id="paymentMethod"
                  name="payment"
                  value={option.id}
                  checked={paymentMethod === option.id}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                />
                <label htmlFor={option.id}>
                  <i className={option.icon}></i>
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.formSection}>
          <h3>
            <i className="fas fa-clipboard-list"></i> 附加信息
          </h3>
          <div className={styles.formGroup}>
            <label htmlFor="notes">订单备注（可选）</label>
            <textarea
              id="notes"
              name="notes"
              rows="3"
              placeholder="如有特殊要求，请在此处说明"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>
      </form>

      <LoginModal 
        isOpen={isLoginModalOpen} 
        onClose={handleCloseModal} 
        onLoginSuccess={handleLoginSuccess} 
      />

      <AlertModal 
        isOpen={isPaymentModalOpen} // 通过 props 传递 isPaymentModalOpen
        message="请在新页面完成支付，正在检查支付结果..." 
        onClose={handlePaymentModalClose} 
      />
    </div>
  );
};

export default OrderForm;